import { waitForTx } from '@waves/waves-transactions';
import { makeAutoObservable, runInAction } from 'mobx';
import { NODE_URL_MAP } from 'src/constants';

import { tokenInfo, ASSET_ID } from 'src/constants/common';

import noImage from "../assets/noImage.png";
import RootStore from './RootStore';
import { PinataSDK } from 'pinata';

const MAINNET_ADDRESS = NODE_URL_MAP.W;
const MAINNET_CREATE_DAPP = '3PFQjjDMiZKQZdu5JqTHD7HwgSXyp9Rw9By';

const mainNetMarketDappAddress = '3PATXDVE53rKJtkwXYV3tvuLVS2fDtGtKCc';
const proxyContractAddress = '3P6fAxtw12pjFhayEfpcUWxgu2BHVCeP78A';

// wrapper contracts
const nftDuckWrapper = '3P3pDosq4GCwfJkvq4yqKvvoTwmoqc9qPmo';
const nftSignArtWrapper = '3PPgeYTWMRZ7S7jSLpnVuKKB9r6XuDk3ndC';

// waves ducks issue contracts
const MAINNET_DUCKS_INCUBATOR_DAPP = '3PEktVux2RhchSN63DsDo4b4mz4QqzKSeDv';
const MAINNET_DUCKS_BREEDER_DAPP = '3PDVuU45H7Eh5dmtNbnRNRStGwULA7NY6Hb';

interface IBidParams {
    key: string;
    owner: string;
    amount: number;
    assetId: ASSET_ID.WAVES | ASSET_ID.PUZZLE | ASSET_ID.USDN | ASSET_ID.EGG;
    usdnPrice: number;
    usdtPrice: number;
}

export enum NftType {
    ORIGIN = 'origin',
    SIGN_ART = 'signart',
    DUCKS = 'ducks',
    WAVES_PUNKS = 'wavesPunks',
    WAVES_LANDS = 'wavesLands',
    WAVES_DOMAINS = 'wavesDomains'
};

interface ICardParams {
    name: string;
    quantity: number;
    type: NftType;
    description: string;
    assetId: string;
    originalId?: string;
    issuer: string;
    creator: string;
    seller?: string;
    sellerComment?: string;
    owner: string;
    image?: string;
    originalSource?: string;
    data: string;
    likes?: string;
    identical?: {items: string[]};
    buyOutPrice?: number;
    buyOutAssetId?: string;
    startSaleAt?: number;
    issueTimestamp?: number;
    bids?: IBidParams[];
    characteristics?: {id: string, title: string, value: any}[];
    achievements?: string[];
    sourceList?: string;
    verified?: boolean;

    pinned?: boolean;
    pinnedHeight?: number;

    signArtWrapp?: boolean;
    wavesDucksWrapp?: boolean;

    isGroup?: boolean;
    groupSize?: number;
    groupAvailable?: number;
}

interface ISendParams {
    recipient: string;
    message?: string;
    assetId: string;
    amount: number;
}

class CardStore {
    rootStore: RootStore;

    name: string;
    quantity: number;
    type: NftType;
    description: string;
    assetId: string;
    originalId: string;
    issuer: string;
    creator: string;
    seller: string;
    sellerComment: string;
    owner: string;
    image: string;
    originalSource: string;
    data: string;
    likes: string;
    identical: {items: string[]};
    likeCount: number;
    buyOutPrice: number;
    buyOutAssetId: string;
    startSaleAt: number;
    issueTimestamp: number;
    value: number | string = '';
    bids: IBidParams[];
    characteristics: {id: string, title: string, value: any}[];
    achievements: string[];
    sourceList: string;
    verified: boolean;

    pinned: boolean;
    pinnedHeight: number;

    signArtWrapp: boolean;
    wavesDucksWrapp: boolean;

    isGroup: boolean;
    groupSize: number;
    groupAvailable: number;

    constructor(params: ICardParams, rootStore: RootStore) {
        makeAutoObservable(this);

        this.rootStore = rootStore;

        this.type = params.type || NftType.ORIGIN;
        this.name = params.name;
        this.originalId = params.originalId || '';
        this.quantity = params.quantity;
        this.description = params.description;
        this.assetId = params.assetId;
        this.issuer = params.issuer;
        this.seller = params.seller || '';
        this.sellerComment = params.sellerComment || '';
        this.owner = params.owner;
        this.image = params.image || '';
        this.originalSource = params.originalSource || '';
        this.data = params.data;
        this.creator = params.creator;
        this.likes = params.likes || '';
        this.identical = params.identical || {items: [params.assetId]};
        this.likeCount = params.likes ? params.likes.split(',').length : 0;
        this.buyOutPrice = params.buyOutPrice || 0;
        this.startSaleAt = params.startSaleAt || 0;
        this.buyOutAssetId = params.buyOutAssetId || '';
        this.issueTimestamp = params.issueTimestamp || 0;
        this.sourceList = params.sourceList || '';
        this.verified = params.verified || false;

        this.pinned = params.pinned || false;
        this.pinnedHeight = params.pinnedHeight || 0;

        this.signArtWrapp = !!params.signArtWrapp;
        this.wavesDucksWrapp = !!params.wavesDucksWrapp;

        // multiple NFT
        this.isGroup = params.isGroup || false;
        this.groupSize = params.groupSize || 0;
        this.groupAvailable = params.groupAvailable || 0;

        this.characteristics = params.characteristics || [];
        this.achievements = params.achievements || [];

        this.bids = params.bids || [];
    }
    
    valueChange(value: number): void {
        this.value = value;
    }

    get sellerName(): string {

        if (this.seller?.indexOf('3P') === 0) {
            return this.seller.substring(0, 3) + '...' + this.seller.slice(-3);
        }

        return '';
    }

    get marketLink(): string {
        return this.assetId ? `/nft/${this.assetId}` : '#';
    }

    get isVideo(): boolean {
        return this.imageUrl.indexOf('mp4') >= 0;
    }

    get imageUrl(): string {
        switch (this.type) {
            case NftType.SIGN_ART:
                return this.image ? 'https://signart.infura-ipfs.io/ipfs/' + this.image : noImage;
            case NftType.DUCKS:
                //parse genotype example: DUCK-HHHHHHHH-GB
                const parts = this.name.split('-');
                const isDruck = parts[1].indexOf('I') >= 0;

                return `https://wavesducks.com/api/v1/ducks/${parts[1]}.svg?onPerch=false&color=${parts[2][1]}&jedi=false${isDruck ? '&druck=1' : ''}`;
            default:
                const duckRegExp = /DUCK-([A-Z]+)-([A-Z]+)/;
                const duckGen = this.description.match(duckRegExp);
                if (duckGen?.[1] && this.image) {
                    const gen = duckGen?.[1];
                    return gen.indexOf('I') >= 0 ?
                        this.image + '&druck=1' :
                        this.image;
                }
                if (this.signArtWrapp) {
                    return this.image.replace('https://ipfs.io/', 'https://signart.infura-ipfs.io/');
                }
                if (this.image.indexOf('https://ipfs.io/ipfs') === 0 && this.image.indexOf('/Qm') === -1) {
                    return this.image.replace('https://ipfs.io/', 'https://magenta-official-dragon-804.mypinata.cloud/');
                }
                return this.image || noImage;
        }
    }

    get cid(): string {
        if (this.image.indexOf('https://ipfs.io/ipfs') === 0 && this.image.indexOf('/Qm') === -1) {
            const parts = this.image.replace('https://ipfs.io/ipfs/', '').split('/');
            return parts?.[0] || '';
        }
        return '';
    }

    get originalUrl(): string {
        switch (this.type) {
            case NftType.SIGN_ART:
                return this.originalSource ? 'https://signart.infura-ipfs.io/ipfs/' + this.originalSource : '';
            case NftType.ORIGIN:
                return this.originalSource || '';
        }

        return '';
    }

    get bgColor(): string {
        let color = '';

        if (this.type === NftType.DUCKS) {
            color = this.name[this.name.length-1];
        } else if (this.description) {
            const duckRegExp = /DUCK-([A-Z]+)-([A-Z]+)/;
            const duckGen = this.description.match(duckRegExp);
            if (duckGen?.[2]) {
                color = duckGen[2][duckGen[2].length-1];
            }
        }

        switch(color) {
            case 'Y': return 'rgb(248, 238, 157)';
            case 'B': return 'rgb(173, 216, 230)';
            case 'G': return 'rgb(217, 246, 179)';
            case 'R': return 'rgb(255, 160, 122)';
            default:
                return '#c6c9f4';
        }
    }

    get collectionName(): string {
        if (this.data) {
            const parsedData = JSON.parse(this.data.replaceAll('\'', '"'));

            return parsedData?.collection?.indexOf('_') !== -1 ? 
                parsedData.collection.split('_')?.[1] :
                parsedData.collection;
        }
        return '';
    }
    
    get hasCollectionUrl(): boolean {
        return this.issuer === MAINNET_CREATE_DAPP;
    }

    get collectionId(): string {
        if (this.data) {
            const parsedData = JSON.parse(this.data.replaceAll('\'', '"'));

            return parsedData?.collection || '';
        }

        return '';
    }

    get collectionUrl(): string {
        if (this.data) {
            const parsedData = JSON.parse(this.data.replaceAll('\'', '"'));
            const [owner, collection] = parsedData.collection.split('_');

            return owner && collection ? `/collection/${owner}/${collection}` : '';
        }

        return '';
    }

    get isFree(): boolean {
        return this.buyOutAssetId === 'free' && this.buyOutPrice === 0;
    }

    get price(): string {
        if (this.isFree) {
            return 'Free';
        }
        if (tokenInfo[this.buyOutAssetId]?.decimals) {
            return this.buyOutPrice / tokenInfo[this.buyOutAssetId]?.decimals + '';
        }
        return 'Not for sale';
    }

    get usdnPrice(): number {
        if (tokenInfo[this.buyOutAssetId]?.decimals) {
            return ((this.buyOutPrice / tokenInfo[this.buyOutAssetId]?.decimals) * this.rootStore.usdnPrices[this.buyOutAssetId]);
        }

        return 0;
    }

    get usdtPrice(): number {
        if (tokenInfo[this.buyOutAssetId]?.decimals) {
            return ((this.buyOutPrice / tokenInfo[this.buyOutAssetId]?.decimals) * this.rootStore.usdtPrices[this.buyOutAssetId]);
        }

        return 0;
    }

    get bidsInfo(): string {
        if (this.bids?.length) {
            return `${this.bids.length} offer${this.bids.length > 1 ? 's': ''}${this.bids.length > 1 ? ' from': ''} `;
        }
        return '';
    }

    get isOnSell(): boolean {
        return this.isFree || !!this.buyOutPrice;
    }

    get isUserLiked(): boolean {
        const currentUser = this.rootStore.accountStore.address;
        return !!currentUser && this.likes.indexOf(currentUser) >=0;
    }

    get isAllowLikes(): boolean {
        return !!this.seller;
    }

    get isDuck(): boolean {
        return [
            MAINNET_DUCKS_INCUBATOR_DAPP,
            MAINNET_DUCKS_BREEDER_DAPP
        ].indexOf(this.issuer) !== -1 || this.creator === nftDuckWrapper;
    }

    get isPinned(): boolean {
        if (this.pinned && this.pinnedHeight >= this.rootStore.marketStore.blocksHeight) return true;
        return false;
    }

    async like(cb?: Function): Promise<void> {
        if (!this.isUserLiked) {
            try {
                const txId = await this.rootStore.accountStore.invoke({
                    dApp: mainNetMarketDappAddress,
                    payment: [],
                    call: {
                        function: 'like',
                        args: [
                            {type: 'string', value: this.assetId}
                        ]
                    }
                });
                await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
                runInAction(() => {
                    if (txId) {
                        this.rootStore.notificationStore.notify(`Thanks for your like!`, {
                            type: "success",
                            title: 'Success',
                            link: `https://wavesexplorer.com/tx/${txId}`,
                            linkTitle: "View on Explorer",
                        });
                        cb?.();
                        this.rootStore.marketStore.loadItems();
                    }
                })
            } catch (e: any) {
                runInAction(() => {
                    console.error(e);
                    this.rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                        type: 'error',
                        title: 'Transaction is not completed'
                    });
                });
            }
        } else {
            this.rootStore.notificationStore.notify('You already liked', {
                type: 'error',
                title: 'Validation'
            });
        }
    }

    async sell(cb: Function, assetId: string, sellerComment?: string): Promise<void> {
        const isFree = assetId === 'free';
        if (isFree || (typeof this.value === 'number' && this.value > 0 && (tokenInfo[assetId]))) {
            try {
                const val = isFree ? 0 : ((this.value as number) * tokenInfo[assetId].decimals);
                const txId = await this.rootStore.accountStore.invoke({
                    dApp: proxyContractAddress,
                    payment: [
                        {assetId: this.assetId, amount: '1'}
                    ],
                    call: {
                        function: 'lockAndSellNFT',
                        args: [
                            {type: 'integer', value: isFree ? 0 : val.toFixed()},
                            {type: 'string', value: isFree ? 'free' : tokenInfo[assetId].id},
                            {type: 'string', value: sellerComment || ""}
                        ]
                    }
                });
                await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
                runInAction(() => {
                    if (txId) {
                        this.rootStore.notificationStore.notify(`NFT is on sale`, {
                            type: "success",
                            title: 'Success',
                            link: `https://wavesexplorer.com/tx/${txId}`,
                            linkTitle: "View on Explorer",
                        });
                        cb?.();
                        this.rootStore.marketStore.loadItems();
                    }
                })
            } catch (e: any) {
                runInAction(() => {
                    console.error(e);
                    this.rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                        type: 'error',
                        title: 'Transaction is not completed'
                    });
                });
                
            }
        } else {
            this.rootStore.notificationStore.notify('Enter a price', {
                type: 'error',
                title: 'Validation'
            });
        }
    }

    async send(params: ISendParams, cb?: Function): Promise<void> {
        const {recipient, message, assetId, amount} = params;
        if (recipient) {
            try {
                const txId = await this.rootStore.accountStore.transfer({
                    recipient,
                    attachment: message,
                    assetId,
                    amount
                });
                await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
                runInAction(() => {
                    if (txId) {
                        this.rootStore.notificationStore.notify(`Your NFT sent`, {
                            type: "success",
                            title: `Success`,
                            link: `https://wavesexplorer.com/tx/${txId}`,
                            linkTitle: "View on Explorer",
                        });
                        cb?.();
                    }
                })
            } catch (e: any) {
                runInAction(() => {
                    console.error(e);
                    this.rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                        type: "error",
                        title: "Transaction is not completed",
                    });
                });
            }
        } else {
            this.rootStore.notificationStore.notify('Enter the recipient address', {
                type: 'error',
                title: 'Validation'
            });
        }
    }

    async sendToGatewayHandler(): Promise<void> {
        try {
            const txId = await this.rootStore.accountStore.invoke({
                dApp: '3PPfDHC6hNqDMxRArJvJRkYfC8y6S3rSnYb',
                payment: [{
                    assetId: this.assetId,
                    amount: '1'
                }],
                call: {
                    function: 'sendToGateway',
                    args: [
                        {type: 'string', value: "0x409981e2C0370AA0790CCd4AC90F1143D2a01886"}
                    ]
                }
            });
            await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
            runInAction(() => {
                if (txId) {
                    this.rootStore.notificationStore.notify(`Your NFT sold`, {
                        type: "success",
                        title: `Success`,
                        link: `https://wavesexplorer.com/tx/${txId}`,
                        linkTitle: "View on Explorer",
                    });
                }
            })
        } catch (e) {
            console.error(e);
        }
    }

    async buy(cb?: Function): Promise<void> {
        try {
            let assetId = this.assetId;
            if (this.identical.items.length > 1) {
                assetId = this.identical.items.reverse()[0];
            }
            const txId = await this.rootStore.accountStore.invoke({
                dApp: proxyContractAddress,
                payment: this.isFree ? [] : [
                    {assetId: this.buyOutAssetId, amount: this.buyOutPrice + ''}
                ],
                call: {
                    function: 'buy',
                    args: [
                        {type: 'string', value: this.assetId}
                    ]
                }
            });
            await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
            runInAction(() => {
                if (txId) {
                    this.rootStore.notificationStore.notify(`You bought NFT!`, {
                        type: "success",
                        title: `Success`,
                        link: `https://wavesexplorer.com/tx/${txId}`,
                        linkTitle: "View on Explorer",
                    });
                    cb?.();
                    this.rootStore.marketStore.loadItems();
                }
            })
        } catch(e: any) {
            runInAction(() => {
                console.error(e);
                this.rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                    type: 'error',
                    title: 'Transaction is not completed'
                });
            });
        }
    }

    async burn(cb?: Function): Promise<void> {
        if (!this.seller) {
            try {
                const txId = await this.rootStore.accountStore.invoke({
                    dApp: MAINNET_CREATE_DAPP,
                    payment: [
                        {assetId: this.assetId, amount: '1'}
                    ],
                    call: {
                        function: 'burnNFT',
                        args: []
                    }
                });
                await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
                runInAction(() => {
                    if (txId) {
                        this.rootStore.notificationStore.notify(`NFT burned!`, {
                            type: "success",
                            title: `Success`,
                            link: `https://wavesexplorer.com/tx/${txId}`,
                            linkTitle: "View on Explorer",
                        });
                        cb?.();
                        this.rootStore.marketStore.loadItems();
                    }
                })
            } catch(e: any) {
                runInAction(() => {
                    console.error(e);
                    this.rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                        type: 'error',
                        title: 'Transaction is not completed'
                    });
                });
            }
        }
    }

    async cancel(cb?: Function): Promise<void> {
        try {
            const txId = await this.rootStore.accountStore.invoke({
                dApp: proxyContractAddress,
                payment: [],
                call: {
                    function: 'cancelSellAndUnlock',
                    args: [
                        {type: 'string', value: this.assetId}
                    ]
                }
            });
            await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
            runInAction(() => {
                if (txId) {
                    this.rootStore.notificationStore.notify(`Your NFT deleted from market`, {
                        type: "success",
                        title: `Success`,
                        link: `https://wavesexplorer.com/tx/${txId}`,
                        linkTitle: "View on Explorer",
                    });
                    cb?.();
                    this.rootStore.marketStore.loadItems();
                }
            })
        } catch(e: any) {
            runInAction(() => {
                console.error(e);
                this.rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                    type: 'error',
                    title: 'Transaction is not completed'
                });
            });
        }
    }

    async placeBid(assetId: string, amount: string, cb?: Function): Promise<void> {
        try {
            const txId = await this.rootStore.accountStore.invoke({
                dApp: mainNetMarketDappAddress,
                payment: [
                    {
                        assetId,
                        amount
                    }
                ],
                call: {
                    function: 'placeBid',
                    args: [
                        {type: 'string', value: this.assetId}
                    ]
                }
            });
            await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
            runInAction(() => {
                if (txId) {
                    this.rootStore.notificationStore.notify(`Your bid placed`, {
                        type: "success",
                        title: `Success`,
                        link: `https://wavesexplorer.com/tx/${txId}`,
                        linkTitle: "View on Explorer",
                    });
                    cb?.();
                    this.rootStore.marketStore.loadItems();
                }
            })
        } catch(e: any) {
            runInAction(() => {
                console.error(e);
                this.rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                    type: 'error',
                    title: 'Transaction is not completed'
                });
            });
        }
    }

    async cancelBid(bidId: string, cb?: Function): Promise<void> {
        try {
            const txId = await this.rootStore.accountStore.invoke({
                dApp: mainNetMarketDappAddress,
                payment: [],
                call: {
                    function: 'cancelBid',
                    args: [
                        {type: 'string', value: this.assetId},
                        {type: 'string', value: bidId}
                    ]
                }
            });
            await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
            runInAction(() => {
                if (txId) {
                    this.rootStore.notificationStore.notify(`Your bid canceled`, {
                        type: "success",
                        title: `Success`,
                        link: `https://wavesexplorer.com/tx/${txId}`,
                        linkTitle: "View on Explorer",
                    });
                    cb?.();
                    this.rootStore.marketStore.loadItems();
                }
            })
        } catch(e: any) {
            runInAction(() => {
                console.error(e);
                this.rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                    type: 'error',
                    title: 'Transaction is not completed'
                });
            });
        }
    }

    async acceptBid(bidId: string, cb?: Function): Promise<void> {
        try {
            const txId = await this.rootStore.accountStore.invoke({
                dApp: mainNetMarketDappAddress,
                payment: [],
                call: {
                    function: 'acceptBid',
                    args: [
                        {type: 'string', value: this.assetId},
                        {type: 'string', value: bidId}
                    ]
                }
            });
            await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
            runInAction(() => {
                if (txId) {
                    this.rootStore.notificationStore.notify(`You accepted bid`, {
                        type: "success",
                        title: `Success`,
                        link: `https://wavesexplorer.com/tx/${txId}`,
                        linkTitle: "View on Explorer",
                    });
                    cb?.();
                    this.rootStore.marketStore.loadItems();
                }
            })
        } catch(e: any) {
            runInAction(() => {
                console.error(e);
                this.rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                    type: 'error',
                    title: 'Transaction is not completed'
                });
            });
        }
    }

    async pin(cb?: Function, fn?: string): Promise<void> {
        try {
            const txId = await this.rootStore.accountStore.invoke({
                dApp: mainNetMarketDappAddress,
                payment: [],
                call: {
                    function: fn || 'pin',
                    args: [
                        {type: 'string', value: this.assetId}
                    ]
                }
            });
            await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
            runInAction(() => {
                if (txId) {
                    this.rootStore.notificationStore.notify(`Your NFT pinned`, {
                        type: "success",
                        title: `Success`,
                        link: `https://wavesexplorer.com/tx/${txId}`,
                        linkTitle: "View on Explorer",
                    });
                    cb?.();
                    this.rootStore.marketStore.loadItems();
                }
            })
        } catch(e: any) {
            runInAction(() => {
                console.error(e);
                this.rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                    type: 'error',
                    title: 'Transaction is not completed'
                });
            });
        }
    }
}

export default CardStore;